import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Target, Heart, Compass, ShieldCheck, Users, Mail, Globe, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      {/* Hero */}
      <div className="max-w-3xl">
        <div className="overline mb-3">About FuturePath Learning</div>
        <h1 data-testid="about-heading" className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-blue leading-[1.05]">
          Helping parents design the future of their children.
        </h1>
        <p className="mt-6 text-lg text-brand-muted leading-relaxed">
          FuturePath Learning is an AI-powered platform built for parents who want more than a school
          brochure to decide their child's future. We combine assessment, curriculum guidance,
          career forecasting, communities, tutors and mentors — one warm home for every education decision, from age 2 to adulthood.
        </p>
      </div>

      {/* Motto */}
      <div className="mt-14 rounded-3xl p-10 lg:p-14 bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white text-center">
        <Sparkles size={32} className="mx-auto text-brand-orange mb-4" />
        <div className="text-sm uppercase tracking-[0.3em] text-white/70 mb-3">Our motto</div>
        <p className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl mx-auto">
          "Every child deserves a learning journey designed for who they are —<br className="hidden md:block" />
          <span className="text-brand-orange"> not who the system expects them to be.</span>"
        </p>
      </div>

      {/* Mission & Vision */}
      <div className="mt-14 grid md:grid-cols-2 gap-6">
        <div className="surface p-8">
          <div className="w-12 h-12 rounded-2xl bg-brand-green-soft text-brand-green flex items-center justify-center mb-5"><Target size={22} /></div>
          <h2 className="text-2xl font-semibold text-brand-blue">Our mission</h2>
          <p className="mt-3 text-brand-muted leading-relaxed">
            To give every parent an unbiased, expert, AI-personalised answer to one question:
            "What is the best learning journey for my child?" — and then the tools to walk that journey together.
          </p>
        </div>
        <div className="surface p-8">
          <div className="w-12 h-12 rounded-2xl bg-brand-orange-soft text-brand-orange flex items-center justify-center mb-5"><Compass size={22} /></div>
          <h2 className="text-2xl font-semibold text-brand-blue">Our vision</h2>
          <p className="mt-3 text-brand-muted leading-relaxed">
            A world where every child grows into their strongest, most curious self — guided by
            informed parents, warm communities, and learning that fits the child, not the other way around.
          </p>
        </div>
      </div>

      {/* Goals */}
      <div className="mt-14">
        <div className="overline mb-3">What we're building toward</div>
        <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue">Our goals for the next five years</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            { icon: Users, title: "1 million families guided", body: "Help one million parents across India and the world make confident learning decisions for their children." },
            { icon: Heart, title: "Never sell a bias", body: "Stay unbiased forever — never take money to promote a curriculum, school or ideology." },
            { icon: ShieldCheck, title: "Child-safe by design", body: "Build every feature with child safety, parent consent, and data privacy as first principles." },
            { icon: Compass, title: "20+ countries served", body: "Localise our legal, curriculum and career guidance for 20+ countries and 12+ languages." },
            { icon: Sparkles, title: "AI Education Twin", body: "Give every child a lifelong learning profile that grows with them from age 2 to 22." },
            { icon: Target, title: "1000+ verified mentors", body: "Build the largest network of verified industry mentors accessible to homeschooled and school-going teens." },
          ].map((g) => (
            <div key={g.title} className="surface surface-hover p-6">
              <g.icon size={20} className="text-brand-green mb-3" />
              <h3 className="font-heading font-semibold text-brand-blue text-lg">{g.title}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{g.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Get in touch (generic) */}
      <div className="mt-16 surface p-8 lg:p-12">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div>
            <div className="overline mb-3">Say hello</div>
            <div className="w-16 h-16 rounded-3xl bg-brand-blue text-white flex items-center justify-center"><Sparkles size={24} /></div>
            <h3 className="mt-5 text-2xl font-semibold text-brand-blue">The FuturePath team</h3>
            <div className="text-sm text-brand-muted">Built with care for parents everywhere.</div>
          </div>
          <div className="md:col-span-2">
            <p className="text-brand-blue leading-relaxed">
              We believe <em className="text-brand-green">parents are the most powerful education designers in the world when given the right tools.</em>
            </p>
            <p className="mt-4 text-brand-muted leading-relaxed">
              FuturePath started because families were struggling to piece together curricula, tutors, communities,
              and career direction from a hundred fragmented sources. We built a single warm, unbiased home for that entire
              journey — from a two-year-old's first curiosity to a teenager's university and career plans.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <a href="mailto:futurepathleaening51@gmail.com" data-testid="about-email" className="flex items-center gap-3 p-4 rounded-2xl bg-brand-bg border border-brand-border hover:border-brand-blue transition-colors">
                <Mail size={18} className="text-brand-green" />
                <div className="text-sm">
                  <div className="text-xs text-brand-muted">Email</div>
                  <div className="text-brand-blue font-medium">futurepathleaening51@gmail.com</div>
                </div>
              </a>
              <a href="https://future-path.in" target="_blank" rel="noreferrer" data-testid="about-website" className="flex items-center gap-3 p-4 rounded-2xl bg-brand-bg border border-brand-border hover:border-brand-blue transition-colors">
                <Globe size={18} className="text-brand-orange" />
                <div className="text-sm">
                  <div className="text-xs text-brand-muted">Website</div>
                  <div className="text-brand-blue font-medium">future-path.in</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-16 text-center">
        <h3 className="text-2xl font-semibold text-brand-blue">Ready to design your child's future?</h3>
        <Link to="/assessment" className="btn-primary mt-5 inline-flex">Take the free assessment <ArrowRight size={16} /></Link>
      </div>
    </div>
  );
}
